/**
 * Tracking Health — the problem and the fix, in ONE hotel's own words.
 *
 * These two paragraphs are the first thing an operator reads on a triage card,
 * and until 2026-08-15 they were static maps keyed by problem id alone. The
 * catalog behind them is written for the whole fleet, so they inherited the
 * fleet's hedges — and two of those hedges are actively wrong on a specific
 * hotel's page:
 *
 * 1. **"the booking engine"** where the hotel's engine has a NAME. The page
 *    knows it is Cloudbeds. Making the operator carry that substitution from
 *    the chip at the top of the page to the paragraph halfway down it is work
 *    the screen can do.
 * 2. **"Tag Manager"** on a hotel that has no container. Autumn provisions
 *    GA4-only hotels no GTM container at all, so "add the new domain to the
 *    Conversion Linker in Tag Manager" named a workspace nobody could open —
 *    the reader either hunts for it or concludes the tool is confused. The
 *    real surface on those hotels is the GA4 tag's own **Configure your
 *    domains** list, which is a different screen, under a different login,
 *    with a different name.
 *
 * So the copy is composed per hotel from `HotelTrackingContext` (facts the API
 * ships; the words are ours — see the dashboard-types header). **Where a fact
 * is absent the fleet-wide wording comes back**: an unwalked hotel with no
 * engine credited reads "the booking engine", the phrase the static maps
 * carried, and no sentence ever invents a setup to be specific about. The
 * phrase, not the sentence — composing these per hotel meant rewording the
 * clauses around each hole, so the generic text reads close to what the old
 * maps said without being word-for-word it.
 *
 * And the specificity is never half-applied: a hotel measured by NOTHING is
 * not a GA4-only hotel with one question already answered. It has no console
 * of any kind, so P5, P6 and P15 — the three whose no-container branch goes on
 * to name the GA4 screen instead — take their own `unmeasured` arm rather than
 * inheriting either side's instructions.
 *
 * Division of labour, unchanged: `problemDescription` EXPLAINS (what is wrong),
 * `problemSolution` INSTRUCTS (what to do, no why). Where the fix is automatic
 * the instruction is to approve it, because the plan's automatic steps execute
 * behind approval and nothing else is asked of the operator.
 */

import type { HotelTrackingContext, ProblemId } from "./types";

// ─── The context, reduced to what the copy branches on ───────────────────────

/**
 * How this hotel is measured, as the copy cares about it.
 *
 * `gtm` and `ga4-only` are not merely different IDs — they are different
 * consoles, so every sentence that names a place to click has to branch on
 * them. `unmeasured` is the third real state and the one that must never be
 * silently folded into either: a hotel with neither a container nor a GA4 tag
 * has no measurement surface at all, and instructions to edit one would send
 * an operator looking for something that does not exist.
 *
 * `unknown` is not a state of the hotel at all — it is the absence of a
 * reading, and it exists so that absence can never be mistaken for one of the
 * three. A run whose hotel row could not be loaded knows nothing about the
 * setup; printing `unmeasured`'s sentences there would tell an operator that
 * nothing measures a hotel that may be perfectly instrumented. It takes the
 * fleet-wide wording instead, which claims no console either way.
 */
export type MeasurementSetup = "gtm" | "ga4-only" | "unmeasured" | "unknown";

export interface HotelCopyContext {
  /**
   * The ONE engine a sentence is allowed to name.
   *
   * Null when none is credited — a real state (unwalked, or an OTHER claim)
   * and not a lookup failure — and null again when SEVERAL are, which is the
   * less obvious half. Every sentence the copy fills is singular ("… ask
   * whether **it** can send", "the template for …"), so a joined list makes
   * each of them ungrammatical and names artefacts that exist for neither
   * engine. The fleet-wide phrase is true of both; "Cloudbeds and Mews" is
   * true of neither. Multi-engine hotels are exceedingly rare — rare enough
   * that ADR 0023 retired the problem that checked for them — and their names
   * are still shown, above the paragraph, where a list is a list.
   */
  engine: string | null;
  /** Every credited engine, in chip order, for the setup strip. A LABEL can
   *  name two engines honestly; a sentence cannot, which is why this is a
   *  separate field from `engine` rather than the same one joined. */
  engineNames: string[];
  /** True when every credited engine came from the onboarding claim rather
   *  than an observed booking route. The name is still the best one we have —
   *  it just has not been seen in the wild, which the surface can disclose. */
  engineFromClaimOnly: boolean;
  setup: MeasurementSetup;
}

/**
 * Both facts must agree before a hotel counts as GTM-tracked — the same rule
 * the backend applies when it decides which steps a plan may even propose
 * (`hotelOwnedSurfaces`). `trackingMode` is an enrollment-time snapshot known
 * to go stale after a migration, so a hotel carrying a real container id is
 * GTM-tracked whatever the path says. Keeping the two readings identical is
 * what stops the card promising a fix the plan will not offer.
 */
export function hotelCopyContext(
  hotel: HotelTrackingContext | null | undefined,
): HotelCopyContext {
  if (!hotel) {
    return {
      engine: null,
      engineNames: [],
      engineFromClaimOnly: false,
      setup: "unknown",
    };
  }
  const names = hotel.engines.map((chip) => chip.displayName).filter(Boolean);
  const setup: MeasurementSetup =
    hotel.trackingMode === "gtm" || hotel.gtmContainerPublicId
      ? "gtm"
      : hotel.trackingMode === "ga4-only"
        ? "ga4-only"
        : "unmeasured";
  return {
    engine: names.length === 1 ? names[0] : null,
    engineNames: names,
    engineFromClaimOnly:
      hotel.engines.length > 0 &&
      hotel.engines.every((chip) => chip.source === "claim"),
    setup,
  };
}

// ─── Phrase helpers ──────────────────────────────────────────────────────────

/** The engine as a complete noun phrase — its name, or the words the copy
 *  carried before it could be told one. Only ever used where BOTH read as
 *  English ("the domain of {engine}", never "the {engine} domain"). */
function engineNoun(ctx: HotelCopyContext): string {
  return ctx.engine ?? "the booking engine";
}

/**
 * Whether this hotel is KNOWN to have no Tag Manager container of ours.
 *
 * The question every sentence that names a console has to ask, and it is
 * phrased around what we know rather than what the hotel is because those come
 * apart: a hotel we could not read is not a hotel without a container. So
 * `unknown` answers false and keeps the fleet-wide sentence — the same words
 * the card printed before it could be told anything about the setup. Only a
 * hotel we actually read, and read as having no container, is told so.
 *
 * It answers the CONTAINER question and nothing else, so it is deliberately
 * true on `unmeasured` too. Where the no-container branch goes on to name the
 * GA4 console as the surface instead — P5, P6 and P15 all do — `unmeasured`
 * has to be split off FIRST: that hotel has no GA4 tag either, and sending it
 * to a GA4 screen is the same failure as sending a GA4-only hotel to Tag
 * Manager, one console further along.
 */
function knownToHaveNoContainer(ctx: HotelCopyContext): boolean {
  return ctx.setup === "ga4-only" || ctx.setup === "unmeasured";
}

/**
 * The one-line summary of WHICH CONSOLE measures this hotel, for the strip
 * above the problem.
 *
 * It answers that and only that. It deliberately does NOT say whether the
 * console is ours to write to, and the restraint is load-bearing: this strip
 * once read "a Tag Manager container **of ours**" / "**ours to publish**",
 * which is two separate claims too far. `HotelTrackingContext` carries no
 * access mode, so a `ui-only` hotel — where `surfaceIsWritable(hotel, "gtm")`
 * is false and the access chip at the top of the same page says "nothing here
 * can publish a container" — got a strip contradicting the chip beside it. And
 * even on a `programmatic` hotel the recorded container id is not necessarily
 * under our GTM account: ADR 0028's executor refuses a container outside
 * `GTM_ACCOUNT_ID`, which is the live shape of a hotel whose container is
 * really the customer's.
 *
 * Who may publish is answered where it is actually known — the access chip for
 * the hotel, `canExecute.reason` for each individual step. Two authorities on
 * one screen is one too many, and this is the one with the least information.
 *
 * Deliberately carries no ids either: an operator does not act on
 * `GTM-N4GFHKTZ`, and `humanizeEvidence` already strips them out of every
 * finding for the same reason.
 */
export function setupSummary(ctx: HotelCopyContext): string {
  switch (ctx.setup) {
    case "gtm":
      return "tracked through a Tag Manager container";
    case "ga4-only":
      return "GA4-only — no Tag Manager container on record";
    case "unmeasured":
      return "no Tag Manager container and no GA4 tag on record";
    default:
      return "how this hotel is measured could not be read";
  }
}

/** The hover under the strip: where the domain settings actually live for this
 *  hotel, which is the fact the fleet-wide copy kept getting wrong. Same rule
 *  as `setupSummary` — it names the console, never who can publish to it. */
export function setupHint(ctx: HotelCopyContext): string {
  switch (ctx.setup) {
    case "gtm":
      return "Cross-domain and conversion tags live on the Conversion Linker in this hotel's Tag Manager container. Whether we can publish to it is a separate question — the access chip at the top of the page answers it for the hotel, and each step answers it for itself.";
    case "ga4-only":
      return "Autumn provisions this hotel no Tag Manager container, so there is no Conversion Linker to edit. Its cross-domain list is the GA4 tag's own “Configure your domains” setting, in the GA4 admin.";
    case "unmeasured":
      return "Neither a Tag Manager container nor a GA4 measurement id is recorded for this hotel, so nothing of ours is measuring it yet. Treat any instruction to edit a tag as a question about what this hotel actually runs.";
    default:
      return "This hotel's setup could not be read, so the wording below is the fleet-wide one and is specific to no console in particular. Open the hotel's own page before following any instruction that names one.";
  }
}

/** The disclosure beside a name that no walk has confirmed. */
export const ENGINE_FROM_CLAIM_HINT =
  "Credited from the onboarding claim — no booking route has been walked on this hotel, so the engine is what the customer told us, not what we observed.";

/** The sentence appended wherever a fix depends on a measurement surface the
 *  hotel does not have. */
const NOTHING_MEASURES_IT =
  "Nothing of ours measures this hotel yet — neither a Tag Manager container nor a GA4 tag is recorded for it — so establish that first; there is no tag here to repair.";

/** A GTM-shaped problem landing on a hotel with no container is not a fix
 *  instruction, it is a contradiction, and saying so is more useful than any
 *  instruction would be. */
const NO_CONTAINER_CONTRADICTION =
  "This hotel has no Tag Manager container of ours, so a finding here means our record of how it is measured is out of date — check whether a container was added before working the steps.";

/** The same contradiction one console wider, stated but not yet acted on. On a
 *  hotel we have read as measured by nothing at all, a tag-shaped finding
 *  cannot be worked on either console — there is no container AND no GA4 tag
 *  on record to open. Naming a screen here would be a guess about which one
 *  this hotel turns out to have. */
const NOTHING_ON_RECORD_MEANS =
  "Nothing of ours is on record measuring this hotel — no Tag Manager container and no GA4 tag — so a finding about its tags means something is installed that we never recorded.";

/** The contradiction where the WHOLE problem is tag-shaped, so nothing on the
 *  card can be worked until the record is right.
 *
 *  Kept separate from `NOTHING_ON_RECORD_MEANS` because a problem with a half
 *  we can still act on must not borrow this closing line — "before working any
 *  of these steps" would retract the very instruction it sits beside. That is
 *  a whole-card rule, not a per-tile one: an operator reads the problem and
 *  the fix as one thing, so a stop in either tile contradicts an instruction
 *  in the other just as loudly. P15 is the one problem with an actionable half
 *  (the Google Ads account is ours on every hotel in the fleet), so it takes
 *  `NOTHING_ON_RECORD_MEANS` in BOTH tiles and sequences the work itself. */
const NOTHING_ON_RECORD_CONTRADICTION = `${NOTHING_ON_RECORD_MEANS} Find out what actually runs on this hotel before working any of these steps.`;

// ─── What is wrong ───────────────────────────────────────────────────────────

const DESCRIPTIONS: Record<ProblemId, (ctx: HotelCopyContext) => string> = {
  "1": (ctx) => {
    const missing =
      ctx.setup === "gtm"
        ? "The code to look for is this hotel's Tag Manager container snippet."
        : ctx.setup === "ga4-only"
          ? "This hotel has no Tag Manager container — it is measured by the GA4 tag directly, so that is the code to look for."
          : "";
    return join(
      `The tracking code is missing from the hotel's website, from ${engineNoun(ctx)}, or from both. Until it is added, bookings from those pages are not counted.`,
      missing,
    );
  },
  "2": (ctx) =>
    `The hotel's booking flow moved to a different web address without warning — either their website or ${engineNoun(ctx)} changed domains. Tracking still points at the old address, so bookings have stopped being counted.`,
  "3": (ctx) =>
    `Google Analytics sees visitors on this hotel's pages but never a completed booking. ${sentenceCase(engineNoun(ctx))} is not reporting purchases, so every booking looks like it never happened.`,
  "4": (ctx) =>
    `Bookings are being counted, but without their dollar value or currency. Guests can also pass through an in-between page on the way to ${engineNoun(ctx)}, and that extra step can drop the value. Revenue reports read $0 or wrong.`,
  "5": (ctx) =>
    `When a guest moves from the hotel's website to ${engineNoun(ctx)}, tracking loses them and treats them as a brand-new visitor — so their booking cannot be credited to the ad they clicked.`,
  "6": (ctx) =>
    ctx.setup === "unmeasured"
      ? NOTHING_ON_RECORD_CONTRADICTION
      : ctx.setup === "ga4-only"
        ? `The GA4 tag is not carrying a guest's ad click across from the website to ${engineNoun(ctx)}, so bookings are not matched back to ads. This hotel has no Tag Manager container, so the domains that must be carried are set in the GA4 tag's own “Configure your domains” list rather than on a Conversion Linker.`
        : `The Conversion Linker — the piece that carries a guest's ad click across to ${engineNoun(ctx)} — is missing or lists the wrong domains, so bookings are not matched back to ads.`,
  "7": () =>
    "The hotel's cookie banner blocks tracking for every guest who does not press Accept. Most guests never do, so most visits and bookings go uncounted.",
  "8": (ctx) =>
    `The booking form from ${engineNoun(ctx)} is embedded inside the hotel's page in a frame, and bookings made inside it may never reach tracking.`,
  "9": (ctx) =>
    knownToHaveNoContainer(ctx)
      ? NO_CONTAINER_CONTRADICTION
      : "This hotel's Tag Manager container is broken — tags that should fire are missing, paused, or wrong — so tracking is not recording what it should.",
  "10": () =>
    "The hotel's Google Analytics property is set up wrong — a wrong measurement ID, duplicate streams, or data routed to the wrong place — so numbers land in the wrong spot or get split.",
  "11": (ctx) =>
    `${sentenceCase(engineNoun(ctx))} is not sending booking events for this hotel, even though it sends them for other hotels on the same engine.`,
  "12": (ctx) =>
    ctx.engine
      ? `This hotel books through ${ctx.engine}, an engine we have not worked with before, so nobody knows yet whether its bookings can be tracked.`
      : "This hotel uses a booking engine we have not worked with before, so nobody knows yet whether its bookings can be tracked.",
  "13": () =>
    "The Google Ads settings that count bookings are wrong — for example counting only one booking per guest, recording $1 for bookings with no price, or click labelling switched off — so the totals are wrong even where tracking works.",
  "14": (ctx) =>
    `The hotel's website or its way through to ${engineNoun(ctx)} cannot be reached — the site is down, the booking link is dead, or something is blocking visitors from getting through.`,
  "15": (ctx) =>
    ctx.setup === "unmeasured"
      ? NOTHING_ON_RECORD_MEANS
      : ctx.setup === "ga4-only"
        ? "The tags are sending this hotel's data to the wrong place: the IDs on its GA4 tag do not match the hotel's Google Ads account, so bookings are credited to the wrong account."
        : "The tags are sending this hotel's data to the wrong place: the IDs inside Tag Manager do not match the hotel's Google Ads account, so bookings are credited to the wrong account.",
  // No engine named here, deliberately: P16 counts ROUTES, and an engine is
  // not one — "Cloudbeds is one of them" is a category error, and a hotel can
  // perfectly well run two routes on a single engine. The retired problem gains
  // nothing from being specific about the wrong noun.
  "16": () =>
    "This hotel takes bookings through more than one route, and one may be broken while another looks fine. No longer checked; kept so old records still read.",
  // Context-free by nature, not by omission (ADR 0039): the problem is the
  // Google Ads account, which is ours on every hotel in the fleet whatever
  // measures the hotel — so there is no engine to name and no console to
  // branch on. Named by Rule 6 as it fires; no Validator check speaks to it.
  "17": () =>
    "Google Ads delivered too few clicks to this hotel in the last week. The campaigns are not serving as they should — budget, billing, status, policy or bids — which is a Google Ads problem, not a tracking one.",
};

// ─── What to do ──────────────────────────────────────────────────────────────

const SOLUTIONS: Record<ProblemId, (ctx: HotelCopyContext) => string> = {
  "1": (ctx) => {
    if (ctx.setup === "unmeasured") return NOTHING_MEASURES_IT;
    const code =
      ctx.setup === "gtm"
        ? "the Tag Manager container snippet"
        : ctx.setup === "ga4-only"
          ? "the GA4 tag"
          : "the tracking code (Google Analytics, Tag Manager, or both)";
    return `If we have access to the website or to ${engineNoun(ctx)}, add ${code} ourselves where it is missing. Otherwise, send the customer the install instructions and wait.`;
  },
  "2": (ctx) => {
    const which = `Work out which one changed — the website or ${engineNoun(ctx)}.`;
    if (ctx.setup === "ga4-only") {
      return `${which} This hotel has no Tag Manager container, so there is no Conversion Linker to edit — add the new domain in the GA4 tag's own “Configure your domains” list.`;
    }
    if (ctx.setup === "unmeasured") {
      return `${which} ${NOTHING_MEASURES_IT}`;
    }
    return `${which} Then update both lists: add the new domain to the Conversion Linker in Tag Manager, and add it in the GA4 cross-domain settings.`;
  },
  "3": (ctx) =>
    knownToHaveNoContainer(ctx)
      ? `This hotel has no Tag Manager container, so the purchase event has to come from ${engineNoun(ctx)} itself — contact its support and have them confirm a completed booking sends one. Where a container is known to work on this engine, the plan proposes building one.`
      : `Contact support for ${engineNoun(ctx)} and have them confirm that a completed booking sends a purchase event. Where the container is simply missing the purchase tag, approve the proposed fix instead.`,
  "4": (ctx) =>
    knownToHaveNoContainer(ctx)
      ? `Approve the Google Ads fix — that account is ours, and the $0 default is what stops an unpriced booking being recorded as $1. The value itself has to come from ${engineNoun(ctx)}: with no container of ours to repair, that is a change on the engine's side or by whoever runs its tagging.`
      : "Approve the proposed fixes — they repair the tag's value and currency, the $0 default on the Google Ads side, and the Conversion Linker's domain list. Only where we do not run the tagging: send the exact domain list to whoever does, then wait.",
  "5": (ctx) =>
    ctx.setup === "unmeasured"
      ? NOTHING_MEASURES_IT
      : knownToHaveNoContainer(ctx)
        ? `There is no Conversion Linker on this hotel, so there is one list to check: the GA4 tag's own “Configure your domains”. It must carry the website, ${engineNoun(ctx)}, and any domain in between. Add what is missing by hand.`
        : `Check both lists: the Conversion Linker's list in Tag Manager and the GA4 cross-domain list must each carry the website, ${engineNoun(ctx)}, and any domain in between. Add what is missing.`,
  "6": (ctx) =>
    ctx.setup === "unmeasured"
      ? NOTHING_ON_RECORD_CONTRADICTION
      : knownToHaveNoContainer(ctx)
        ? `Open the GA4 tag's “Configure your domains” settings and add the domain of ${engineNoun(ctx)}, plus any domain the guest passes through in between. Nothing here can publish that change — it is made in the GA4 admin by hand.`
        : "Approve the proposed fix — it corrects the Conversion Linker and its domain list and republishes the container.",
  "7": (ctx) =>
    `Contact the customer and ask them to remove the cookie banner. If we have access to ${engineNoun(ctx)} or their website, remove it ourselves.`,
  "8": (ctx) =>
    knownToHaveNoContainer(ctx)
      ? "This hotel has no container of ours to bridge the events through, so there is nothing to approve here — contact an engineer and hand this hotel over for triage."
      : "Where the page allows it, approve the proposed fix — it bridges the events out through our container. If it cannot be bridged, contact an engineer and hand this hotel over for triage.",
  "9": (ctx) =>
    knownToHaveNoContainer(ctx)
      ? NO_CONTAINER_CONTRADICTION
      : "Approve the proposed fix — it restores the missing tags and triggers and republishes the container.",
  "10": () =>
    "Approve the proposed fix — we manage these Analytics accounts, so it corrects the streams and IDs directly. Only if the property is customer-owned: send them the exact settings and wait.",
  "11": (ctx) =>
    `Contact the vendor behind ${engineNoun(ctx)} and ask them to turn on these events for this hotel. Other hotels on the same engine already send them, so the engine can. Wait for their answer.`,
  "12": (ctx) =>
    `Engineering researches ${engineNoun(ctx)}. Ask its vendor to confirm in writing that completed bookings are sent with their value — do not accept “the code is installed”.`,
  "13": () =>
    "Approve the proposed fix — this hotel's Google Ads account is ours, like every account in the fleet, so it corrects the settings directly.",
  "14": (ctx) =>
    `Website unreachable: contact the customer, then check again. If it is ${engineNoun(ctx)} that cannot be reached: contact the customer and whoever operates it. No way to book found on the site at all: open the site, find the booking link, and record it here.`,
  "15": (ctx) =>
    ctx.setup === "unmeasured"
      ? `Approve the Google Ads fix first — that account is ours, like every account in the fleet, and it is fixable whatever measures the hotel. The tag side cannot be worked yet. ${NOTHING_ON_RECORD_MEANS} Find out what actually runs on this hotel before touching its tags.`
      : knownToHaveNoContainer(ctx)
        ? "Approve the Google Ads fix — that account is ours. The measurement IDs on the GA4 side have to be corrected in the GA4 admin: this hotel has no container of ours to republish."
        : "Approve the proposed fix — it points the tags at the right conversion IDs and labels and republishes.",
  "16": () =>
    "Retired — no longer checked. On old records: each booking route was fixed separately.",
  // Plain text by decision (ADR 0039), verbatim from the catalog's strategy:
  // the cause is one of a handful of things a person sees in a minute inside
  // the account, and none of them is anything an executor here can read or
  // set, so the instruction is the whole fix.
  "17": () => "Go check the Google Ads account.",
};

// ─── The two entry points ────────────────────────────────────────────────────

/**
 * What this problem MEANS on this hotel, before any measurement. The finding
 * lines beside it stay the evidence; this is the explanation an operator reads
 * first. Null for a problem id this map has never met — an unknown id is named
 * honestly by its caller rather than described by a guess.
 */
export function problemDescription(
  problemId: ProblemId,
  ctx: HotelCopyContext,
): string | null {
  return DESCRIPTIONS[problemId]?.(ctx) ?? null;
}

/** The one operational instruction for this problem on this hotel — what to
 *  do, no why. */
export function problemSolution(
  problemId: ProblemId,
  ctx: HotelCopyContext,
): string | null {
  return SOLUTIONS[problemId]?.(ctx) ?? null;
}

// ─── Small string helpers ────────────────────────────────────────────────────

function join(...parts: string[]): string {
  return parts.filter(Boolean).join(" ");
}

/** Capitalises a sentence-initial engine phrase: "the booking engine" opens a
 *  sentence as "The booking engine", while a brand name is left exactly as the
 *  registry spells it (`eviivo`, `iHotelier` — lower-casing or force-casing
 *  either would misname a product). */
function sentenceCase(phrase: string): string {
  return phrase.startsWith("the ")
    ? `The ${phrase.slice(4)}`
    : phrase;
}
